const PREFIX = "vektor-handler:";
const FORMAT_VERSION = 1;

export interface SharedHandlerAction {
    actionType: number;
    configJson: string;
    webhookName: string | null;
}

// Webhooks travel by name only: the id belongs to the sharer's account and the URL is a secret.
export interface SharedHandler {
    name: string;
    eventKey: string;
    cooldownSeconds: number;
    runWhilePossessed: boolean;
    conditionJson: string;
    scheduleJson: string;
    actions: SharedHandlerAction[];
}

export type ShareCodeResult =
    | { ok: true; handler: SharedHandler }
    | { ok: false; errorKey: "AUTOMATION_IMPORT_NOT_A_CODE" | "AUTOMATION_IMPORT_NEWER" | "AUTOMATION_IMPORT_DAMAGED" };

export function encodeShareCode(handler: SharedHandler): string {
    const bytes = new TextEncoder().encode(JSON.stringify({ v: FORMAT_VERSION, ...handler }));
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return PREFIX + btoa(binary);
}

// Whitespace is stripped first: chat clients wrap a long code across lines when it is pasted back.
export function decodeShareCode(code: string): ShareCodeResult {
    const compact = code.replace(/\s+/g, "");
    if (!compact.toLowerCase().startsWith(PREFIX)) return { ok: false, errorKey: "AUTOMATION_IMPORT_NOT_A_CODE" };

    let parsed: any;
    try {
        const binary = atob(compact.slice(PREFIX.length));
        const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
        parsed = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
    } catch {
        return { ok: false, errorKey: "AUTOMATION_IMPORT_DAMAGED" };
    }

    if (typeof parsed?.v !== "number") return { ok: false, errorKey: "AUTOMATION_IMPORT_DAMAGED" };
    if (parsed.v > FORMAT_VERSION) return { ok: false, errorKey: "AUTOMATION_IMPORT_NEWER" };

    const actions = Array.isArray(parsed.actions) ? parsed.actions : null;
    const wellFormed = typeof parsed.name === "string"
        && typeof parsed.eventKey === "string"
        && actions !== null
        && actions.every((action: any) => typeof action?.actionType === "number" && typeof action.configJson === "string");
    if (!wellFormed) return { ok: false, errorKey: "AUTOMATION_IMPORT_DAMAGED" };

    return {
        ok: true,
        handler: {
            name: parsed.name,
            eventKey: parsed.eventKey,
            cooldownSeconds: Number(parsed.cooldownSeconds) || 0,
            runWhilePossessed: parsed.runWhilePossessed === true,
            conditionJson: typeof parsed.conditionJson === "string" ? parsed.conditionJson : "",
            scheduleJson: typeof parsed.scheduleJson === "string" ? parsed.scheduleJson : "",
            actions: actions!.map((action: any) => ({
                actionType: action.actionType,
                configJson: action.configJson,
                webhookName: typeof action.webhookName === "string" ? action.webhookName : null,
            })),
        },
    };
}
